// ---------------------------------------------------------------------------
// Naming patterns for generated Intune objects (016-template-model §6.6).
//
// Lives in src/utils/ rather than src/lib/ (where the mock keeps it) because .gitignore has
// a bare `lib/` rule for build output that silently swallows src/lib/ too — a file there is
// never committed and the app breaks for everyone else.
//
// MUST stay behaviourally identical to the backend's services/naming_service.py.
// The wizard renders its live preview from THIS file for responsiveness, while publish
// renders the real Graph object name from the Python. Generated names are read-only at
// packaging time, so if the two ever disagree an admin approves one name and the tenant
// gets another, with no way to correct it. Any change here needs the same change there,
// and POST /intune-templates/preview-names is the authoritative check.
//
// A pattern is literal text with {Tokens} in it:
//
//   app name    CF-{AppName}-{Arch}-{Version}   ->  CF-ContosoApp-x64-1.0.0
//   group name  CF-{AppName}-{Arch}-{Intent}    ->  CF-ContosoApp-x64-Required
//
// Token scoping follows two rules:
//   1. A group is DURABLE — its membership outlives any single release — so a token whose
//      value changes every release ({Version}) would force a rename AND a reassignment on
//      each update. Barred from group names.
//   2. One Intune app object carries several assignments with different intents, so
//      {Intent} describes an assignment rather than the app. Barred from app names.
//
// There is deliberately no {Prefix} token: a prefix is literal text the admin types
// ("CF-{AppName}"), not a value the system could supply.
// ---------------------------------------------------------------------------

export type NameScope = 'app' | 'group'

export interface NameToken {
  /** Canonical spelling, as inserted by the token picker. */
  token: string
  label: string
  /** Rendered in the live preview and the picker. */
  sample: string
  scopes: NameScope[]
  /** Shown when the token is used in a scope that does not allow it. */
  restriction?: string
  /** What the token renders, for a token whose value is not obvious from its label. */
  hint?: string
}

/** Intune caps a mobileApp displayName at 256 chars. Mirrors MAX_NAME_LENGTH server-side. */
export const MAX_NAME_LENGTH = 256

export const NAME_TOKENS: NameToken[] = [
  { token: '{AppName}', label: 'App name', sample: 'ContosoApp', scopes: ['app', 'group'] },
  { token: '{Publisher}', label: 'Publisher', sample: 'Contoso', scopes: ['app', 'group'] },
  { token: '{Arch}', label: 'Architecture', sample: 'x64', scopes: ['app', 'group'] },
  {
    token: '{Version}',
    label: 'Version',
    sample: '1.0.0',
    scopes: ['app'],
    restriction:
      'Changes on every release — a group carrying it would need renaming and reassigning each update.',
  },
  {
    token: '{Intent}',
    label: 'Assignment intent',
    sample: 'Required',
    scopes: ['group'],
    restriction: "One app object carries several intents, so it cannot be in the app's own name.",
  },
  {
    token: '{DeploymentName}',
    label: 'Deployment name',
    sample: 'AVD',
    scopes: ['app', 'group'],
    hint:
      "Optional. Empty for a package's first deployment, together with the separator next to " +
      'it. Holds the deployment name only when a package has more than one deployment.',
  },
]

export const DEPLOYMENT_NAME_TOKEN = '{DeploymentName}'
/** What one empty {DeploymentName} removes next to itself. naming_service.py uses the same set. */
const SEPARATORS = '-_. '
const EMPTY = '\u0000'

/** Remove each empty {DeploymentName} marker with one separator next to it: the one before it,
 *  else the one after it. naming_service._drop_empty does the same. */
export function dropEmptyDeploymentName(rendered: string): string {
  let out = rendered
  for (let i = out.indexOf(EMPTY); i !== -1; i = out.indexOf(EMPTY)) {
    if (i > 0 && SEPARATORS.includes(out[i - 1])) out = out.slice(0, i - 1) + out.slice(i + 1)
    else if (i + 1 < out.length && SEPARATORS.includes(out[i + 1]))
      out = out.slice(0, i) + out.slice(i + 2)
    else out = out.slice(0, i) + out.slice(i + 1)
  }
  return out
}

/** The marker an empty {DeploymentName} renders as until dropEmptyDeploymentName removes it. */
export const EMPTY_DEPLOYMENT_NAME = EMPTY

/** Defaults (§6.6). All three share a separator so generated names read as one family; the
 *  update-only marker goes FIRST so the two app objects are distinguishable in a sorted list. */
export const DEFAULT_APP_PATTERN = '{AppName}-{Arch}-{Version}'
export const DEFAULT_GROUP_PATTERN = '{AppName}-{Arch}-{Intent}'
export const DEFAULT_UPDATE_APP_PATTERN = '[Update Only]-{AppName}-{Arch}-{Version}'
/** Kept distinct from the app's group pattern on purpose: the population that may be PATCHED
 *  is wider than the one the app may be INSTALLED onto, so sharing a group would install the
 *  app onto devices that were only ever meant to be updated. */
export const DEFAULT_UPDATE_GROUP_PATTERN = '{AppName}-{Arch}-Update-{Intent}'

export const tokensFor = (scope: NameScope): NameToken[] =>
  NAME_TOKENS.filter((t) => t.scopes.includes(scope))

/** Case-insensitive: {Appname} and {AppName} are the same token — nobody should lose ten
 *  minutes to a capital letter. */
const findToken = (raw: string): NameToken | undefined =>
  NAME_TOKENS.find((t) => t.token.toLowerCase() === raw.toLowerCase())

const tokenKey = (t: NameToken) => t.token.slice(1, -1).toLowerCase()

export type NameValues = Record<string, string | undefined>

export const SAMPLE_VALUES: NameValues = Object.fromEntries(
  NAME_TOKENS.map((t) => [tokenKey(t), t.sample]),
)

const TOKEN_RE = /\{[^{}]*\}/g

/**
 * Render a pattern. An UNKNOWN token is left verbatim rather than dropped, so a typo is
 * visible in the preview instead of silently producing a plausible-but-wrong name. A blank
 * or missing value falls back to the token's sample, so a missing arch renders
 * "7-Zip-x64-26.02" rather than "7-Zip--26.02" with a stray separator.
 *
 * {DeploymentName} is the exception: blank means the package's first deployment, so it renders
 * empty, and so it does when no values are given — a preview shows the first deployment's form.
 */
export function renderPattern(pattern: string, values: NameValues = SAMPLE_VALUES): string {
  return dropEmptyDeploymentName(
    (pattern || '').replace(TOKEN_RE, (raw) => {
      const known = findToken(raw)
      if (!known) return raw
      const isName = known.token === DEPLOYMENT_NAME_TOKEN
      const supplied = isName && values === SAMPLE_VALUES ? undefined : values[tokenKey(known)]
      const text = (supplied ?? '').trim()
      return text || (isName ? EMPTY : known.sample)
    }),
  )
}

export interface PatternProblem {
  token: string
  reason: string
}

/**
 * Validate one pattern for one scope. Returns problems (reject) and warnings (allow).
 * Mirrors naming_service.validate_pattern — including the checks that only bite at publish,
 * because the wizard should refuse a pattern the backend would reject rather than let an
 * admin discover it at packaging time when the name is already read-only.
 */
export function validatePattern(
  pattern: string,
  scope: NameScope,
): { problems: PatternProblem[]; warnings: string[] } {
  const problems: PatternProblem[] = []
  const warnings: string[] = []

  if (!pattern || !pattern.trim()) {
    problems.push({ token: '', reason: 'Pattern is empty.' })
    return { problems, warnings }
  }

  const found: string[] = pattern.match(TOKEN_RE) ?? []
  for (const raw of found) {
    const known = findToken(raw)
    if (!known) {
      problems.push({ token: raw, reason: 'Not a known token.' })
    } else if (!known.scopes.includes(scope)) {
      problems.push({
        token: known.token,
        reason: known.restriction ?? `Not available for ${scope} names.`,
      })
    }
  }

  // A pattern with no token is a constant, so every app would get the same displayName and
  // the second publish would collide with the first.
  if (found.length === 0) {
    problems.push({ token: '', reason: 'Pattern must contain at least one token.' })
  } else if (found.every((raw) => raw.toLowerCase() === DEPLOYMENT_NAME_TOKEN.toLowerCase())) {
    // {DeploymentName} alone is a constant too: it is empty for every first deployment.
    problems.push({
      token: '',
      reason:
        "Pattern must contain a token other than {DeploymentName}, which is empty for a package's first deployment.",
    })
  }

  // How many times each known token appears, in first-appearance order. Insertion order is part
  // of the contract: naming_service.py emits the same warnings in the same order.
  const counts = new Map<string, number>()
  for (const raw of found) {
    const known = findToken(raw)
    if (known) counts.set(known.token, (counts.get(known.token) ?? 0) + 1)
  }

  // A repeated token puts the same value in the name twice. One click inserts a token, so this
  // is nearly always a mis-aimed insert rather than a choice — warn, since a tenant could
  // legitimately want "Contoso (Contoso)".
  for (const [token, n] of counts) {
    if (n > 1) {
      warnings.push(`${token} appears ${n} times — the same value is repeated in every name.`)
    }
  }

  // No {Intent} means every intent resolves to ONE group, so Required and Uninstall would
  // target the same devices. A tenant may legitimately want one static group, so warn.
  if (scope === 'group' && found.length > 0 && !counts.has('{Intent}')) {
    warnings.push(
      'Group pattern has no {Intent} — every assignment intent will resolve to the same group ' +
        'name, so Required and Uninstall would target the same devices.',
    )
  }

  // Each release is published as its own Intune app object, so without {Version} every version
  // arrives carrying the last one's name. Not blocked: a tenant that only ever keeps one version
  // live may want a stable name, and displayVersion still tells the objects apart.
  if (scope === 'app' && found.length > 0 && !counts.has('{Version}')) {
    warnings.push(
      'App pattern has no {Version} — each release is published as a separate Intune app object, ' +
        'so every version arrives with the same name and they can only be told apart by opening them.',
    )
  }

  const rendered = renderPattern(pattern)
  if (rendered.length > MAX_NAME_LENGTH) {
    problems.push({
      token: '',
      reason: `Renders to ${rendered.length} characters; Intune allows ${MAX_NAME_LENGTH}.`,
    })
  }

  return { problems, warnings }
}

/** The distinct group names a set of intents renders to, order preserved. Publish creates one
 *  group per distinct NAME, so an {Intent}-less pattern must collapse to a single group here
 *  too or the preview would promise more groups than get created. */
export function distinctGroupNames(
  pattern: string,
  values: NameValues,
  intents: string[],
): string[] {
  const out: string[] = []
  for (const intent of intents) {
    const name = renderPattern(pattern, { ...values, intent })
    if (!out.includes(name)) out.push(name)
  }
  return out
}
